
import { USE_WEBGPU } from "../wgs/globals";
import { SettingsPanel } from "./SettingsPanel";
import * as et from "../application/EventTypes";
import { InternalFeatureFlags } from '../application/FeatureFlags';
import { LightPresets } from "../application/LightPresets";
import i18n from "i18next";
import { isMobileDevice, touchStartToClick } from "../compat";
import { isWebGPUSupported_cached } from "../wgs/wgpu/Support.js";
import { ViewerPanelMixin } from "./ViewerPanelMixin";
import { debounce } from "lodash-es";
import { Prefs3D, Prefs2D, Prefs, FPS_TARGET_MODES, GPU_MEMORY_PRESETS } from '../application/PreferenceNames';
import { EnumType } from "../application/ProfileSettings";
import { displayUnitsEnum, displayUnitsPrecisionEnum } from '../../src/measurement/DisplayUnits';
import { createExperimentalPanel } from './ExperimentalSettings';
import { createElement, setLocalizedText } from './DomUtils';


const av = Autodesk.Viewing;
const avp = av.Private;

let idCounter = 0;

/**
 * Viewer3dSettings Tabs.
 *
 * These constants are used to define the tabs in the ViewerSettingsPanel.
 *
 * @enum {number}
 * @readonly
 */
export let ViewerSettingTab = {
  Navigation: "navigationtab",
  Configuration: "performancetab",
  Appearance: "appearance",
  Environment: "appearance", // Merged into Appearance tab. Kept for backward compatibility.
  Experimental: "experimental"
};

var viewerSettingsPanelInstanceCnt = 0;

/**
 * Options object for settings panel
 * @typedef {Object} ViewerSettingsPanelOptions
 * @property {Preferences} pref - Preferences instance object
 * @property {GlobalManager} globalManager - GlobalManager instance object
 * @property {function} removeEventListener - Event dispatcher function for removing listeners
 * @property {function} addEventListener - Event dispatcher function for adding listeners
 * @property {HTMLElement} container - The container for this panel.
 * @property {function} onRestoreDefaults - Callback for resetting settings to default
 * @property {function} detectIfModelsHaveEdge - Query if current models have edges
 * @property {string} [version] - Version to show on the footer
 * @property {number} [options.width] - Override panel's minimum width
 * @property {number} [options.heightAdjustment] - Override panel's extra content height, to account for non-scrolling elements.
 * /

/**
 * ViewerSettingsPanel
 * This is a panel for displaying the settings for the viewer.
 * @class
 *
 * @param {Autodesk.Viewing.Viewer3D|ViewerSettingsPanelOptions} viewer - the parent viewer
 * @param {Autodesk.Viewing.Model} model - Model to detect whether the viewer renders in 2D or 3D
 * @constructor
 */
export function ViewerSettingsPanel(opts, model) {
  let options = { ...opts };

  this.viewer = null;
  if (opts instanceof Autodesk.Viewing.GuiViewer3D ||
  opts instanceof Autodesk.Viewing.Viewer3D) {
    const viewer = opts;
    options = {
      preferences: viewer.prefs,
      globalManager: viewer.globalManager,
      addEventListener: viewer.addEventListener.bind(viewer),
      removeEventListener: viewer.removeEventListener.bind(viewer),
      container: viewer.container,
      version: viewer.config.viewerVersion,
      onRestoreDefaults: viewer.restoreDefaultSettings.bind(viewer),
      detectIfModelsHaveEdge: detectIfModelsHaveEdge.bind(this),
      loadExtension: viewer.loadExtension.bind(viewer),
      unloadExtension: viewer.unloadExtension.bind(viewer),
      width: 400,
      heightAdjustment: 50 /*title-bar*/ + 40 /*tab-bar*/ + 20 /*footer*/
    };
    this.viewer = viewer;
  }


  this.options = options;
  this.preferences = options.preferences;

  this.is3dMode = !model.is2d();
  const isInputFilePdf = model.getDocumentNode()?.getInputFileType() === 'pdf';
  const isExtractorPdf = model.getDocumentNode()?.data?.extractor?.includes('pdf');
  this.isPdfFile = isInputFilePdf || isExtractorPdf;
  this.visible = false;

  // Keeps track of external registered buttons.
  this._externalButtonIds = [];
  this._externalButtonsLabel = null;

  SettingsPanel.call(
    this,
    this.options.container,
    'ViewerSettingsPanel' + idCounter++ + '-' + viewerSettingsPanelInstanceCnt++, 'Settings',
    options
  );
  this.container.classList.add('viewer-settings-panel');
  this.setGlobalManager(this.options.globalManager);


  this.addTab(ViewerSettingTab.Configuration, "Configuration", { className: "performance" });
  this.addTab(ViewerSettingTab.Navigation, "Navigation", { className: "navigation" });
  this.addTab(ViewerSettingTab.Appearance, "Appearance", { className: "appearance" });

  // Tabs without group headers need extra top spacing
  this.tablesContainer.childNodes[this.tabIdToIndex[ViewerSettingTab.Appearance]].classList.add("pt-4");

  if (!this.is3dMode) {
    this.container.classList.add('for-2d-model');
  }

  this.createRestoreDefaultSettingsButton();

  this.modelPreferenceCount = 0;
  this.createNavigationPanel();
  this.createConfigurationPanel();
  this.createAppearancePanel();
  av.FeatureFlags.isEnabled(InternalFeatureFlags.ShowExperimentalFeatures) && this._initExperimentalTab();

  // Setting Configuration as the default tab
  this.selectTab(ViewerSettingTab.Configuration);

  this.footer = this.createFooter();
  this.createVersionLabel(this.footer);

  // Add events
  this.syncUI = this.syncUI.bind(this);
  this.options.addEventListener(et.RESTORE_DEFAULT_SETTINGS_EVENT, this.syncUI);
  this.options.addEventListener(et.VIEWER_STATE_RESTORED_EVENT, this.syncUI);

  this.updateEdgeToggle = this.updateEdgeToggle.bind(this);
  this.options.addEventListener(et.GEOMETRY_LOADED_EVENT, this.updateEdgeToggle);

  this.sendAnalyticsDebounced = debounce(sendAnalytics, 500);
}

ViewerSettingsPanel.prototype = Object.create(SettingsPanel.prototype);
ViewerSettingsPanel.prototype.constructor = ViewerSettingsPanel;
ViewerPanelMixin.call(ViewerSettingsPanel.prototype);

/**
 * Clean up when the viewer setting  is about to be removed.
 * @override
 */
ViewerSettingsPanel.prototype.uninitialize = function () {
  const doc = this.getDocument();
  this._envSelectUpdateHandler && this.preferences.removeListeners(avp.Prefs3D.LIGHT_PRESET, this._envSelectUpdateHandler);
  doc.removeEventListener("click", this._envSelectDocClickHandler, true);
  doc.removeEventListener("click", this._experimentalMenuDocClickHandler, true);
  this._envSelectUpdateHandler = this._envSelectDocClickHandler = this._envSelectContainer = null;
  this._experimentalMenuDocClickHandler = null;

  this.options.removeEventListener?.(et.RESTORE_DEFAULT_SETTINGS_EVENT, this.syncUI);
  this.options.removeEventListener?.(et.VIEWER_STATE_RESTORED_EVENT, this.syncUI);
  this.options.removeEventListener?.(et.GEOMETRY_LOADED_EVENT, this.updateEdgeToggle);
  SettingsPanel.prototype.uninitialize.call(this);
};


ViewerSettingsPanel.prototype.setVisible = function (show) {
  this.visible = show;
  SettingsPanel.prototype.setVisible.call(this, show);
  show && this.sizeToContent();
};

/**
 * Creates a checkbox element and adds it to the given tab.
 *
 * @param {number} tabId - tab id
 * @param {string} description - the text associated with the checkbox
 * @param {boolean} initialState - initial value for the checkbox (checked or not)
 * @param {function} onchange - callback that is called when the checkbox is changed
 * @param {string} saveKey - name of the preference associated with this checkbox.
 * @param {object|undefined} options - see SettingsPanel.addCheckbox for details
* @returns {string} - it returns the checkbox element.
 *
 */
ViewerSettingsPanel.prototype.addCheckbox = function (tabId, name, description, initialState, onchange, saveKey, options)
{
  const { preferences } = this;
  // Use the stored settings or defaults
  var storedState = preferences[saveKey];
  initialState = typeof storedState === 'boolean' ? storedState : initialState;

  function onChangeCB(checked) {
    if (saveKey) {
      preferences.set(saveKey, checked);
      avp.analytics.track('viewer.settings.changed', {
        setting_name: saveKey,
        setting_value: checked
      });
    }
    onchange && onchange(checked);
  }

  var checkboxId = SettingsPanel.prototype.addCheckbox.call(this, tabId, name, initialState, onChangeCB, description, options);
  var checkBoxElem = this.getControl(checkboxId);
  checkBoxElem.saveKey = saveKey;

  if (saveKey) {
    preferences.addListeners(saveKey, function (value) {
      checkBoxElem.setValue(value);
    }, function (value) {
      checkBoxElem.setValue(value);
      onchange && onchange(value);
    });
  } else
  {
    checkBoxElem.sliderRow.classList.add('logical-group');
  }

  if (preferences.hasTag(saveKey, 'no-storage')) {
    checkBoxElem.sliderRow.classList.add('no-storage');
    this.modelPreferenceCount++;
  }
  return checkboxId;
};

/*
 * @param {string} tabId - Tab to add the new control
 * @param {string} caption - The text associated with the dropdown
 * @param {array} items - List of items for the menu
 * @param {array} values - values corresponding to each item in items
 * @param {number} initialItemIndex - Initial choice.
 * @param {function} onchange - Callback that is called when the menu selection is changed.
 * @param {string} preferenceKey - name of the preference associated with this dropdown.
 * @param {object|undefined} options - Additional options:
 * - insertAtIndex - index at which to insert a new drop down menu
 * @returns {string} ID of a new control.
*/
ViewerSettingsPanel.prototype.addDropDownMenu = function (tabId, caption, items, values, initialItemIndex, preferenceKey, options)
{
  const { preferences } = this;
  if (preferenceKey) {
    // initial value is from preference
    initialItemIndex = values.indexOf(preferences.get(preferenceKey));
  }

  // wrapper to set the preference
  function onChangeCB(evt) {
    if (preferenceKey) {
      if (preferenceKey == "displayUnits") {
        preferences.set(preferenceKey, new EnumType(displayUnitsEnum, values[evt.detail.value]));
      } else
      if (preferenceKey == "displayUnitsPrecision") {
        preferences.set(preferenceKey, new EnumType(displayUnitsPrecisionEnum, values[evt.detail.value]));
      } else
      {
        preferences.set(preferenceKey, values[evt.detail.value]);
      }

      avp.analytics.track('viewer.settings.changed', {
        setting_name: preferenceKey,
        setting_value: values[evt.detail.value]
      });
    }
  }

  const dropDownId = SettingsPanel.prototype.addDropDownMenu.call(this, tabId, caption, items, initialItemIndex, onChangeCB, options);
  const dropDownElem = this.getControl(dropDownId);

  if (preferenceKey) {
    preferences.addListeners(preferenceKey, function (value) {
      dropDownElem.setSelectedIndex(values.indexOf(value));
    }, function (value) {
      dropDownElem.setSelectedIndex(values.indexOf(value));
    });
  } else
  {
    dropDownElem.sliderRow.classList.add('logical-group');
  }

  if (preferences.hasTag(preferenceKey, 'no-storage')) {
    dropDownElem.sliderRow.classList.add('no-storage');
    this.modelPreferenceCount++;
  }
  return dropDownId;
};

/**
 * Creates a row and a slider element and adds it to the given tab.
 *
 * @param {number} tabId - tab id
 * @param {string} caption - the caption associated with the slider
 * @param {string} description - the text associated with the slider
 * @param {boolean} initialValue - initial value for the slider (checked or not)
 * @param {function} onchange - callback that is called when the slider is changed
 * @param {string} saveKey - name of the preference associated with this slider.
 * @returns {string[]} - it returns the row and slider control ids.
 */
ViewerSettingsPanel.prototype.addSliderV2 = function (tabId, caption, description, min, max, initialValue, options, saveKey)
{
  // Use the stored settings or defaults
  const { preferences } = this;
  var storedState = preferences.get(saveKey);
  initialValue = typeof storedState === 'number' ? storedState : initialValue;

  if (saveKey && !Object.prototype.hasOwnProperty.call(preferences, saveKey)) {
    // Add the preferences.
    preferences.add(saveKey, initialValue, ['2d', '3d']);
  }

  function onChangeCB(event) {
    var value = typeof event === 'number' ? event : Number(event.detail.value);
    if (saveKey) {
      preferences.set(saveKey, value);
    }
  }

  var sliderId = SettingsPanel.prototype.addSliderV2.call(this, tabId, caption, description, min, max, initialValue, onChangeCB, options);
  var sliderElem = this.getControl(sliderId[1]);
  sliderElem.saveKey = saveKey;

  if (saveKey) {
    preferences.addListeners(saveKey, function (value) {
      sliderElem.setValue(value);
    });
  } else
  {
    sliderElem.sliderRow.classList.add('logical-group');
  }

  if (preferences.hasTag(saveKey, 'no-storage')) {
    sliderElem.sliderRow.classList.add('no-storage');
    this.modelPreferenceCount++;
  }
  return sliderId;
};

/**
 * Builds a CSS linear-gradient string from a preset's bgColorGradient array ([r1,g1,b1,r2,g2,b2]).
 * @private
 */
function presetGradientCSS(preset) {
  const c = preset.bgColorGradient;
  return `linear-gradient(to bottom, rgb(${c[0]},${c[1]},${c[2]}), rgb(${c[3]},${c[4]},${c[5]}))`;
}

/**
 * Creates a custom dropdown for environment/light preset selection. Each option shows a small gradient
 * swatch derived from the preset's bgColorGradient alongside the preset name.
 * @private
 */
ViewerSettingsPanel.prototype.addEnvironmentDropDown = function (tabId, presets, saveKey) {
  const { preferences } = this;
  const _document = this.getDocument();

  const container = _document.createElement("div");
  container.classList.add("env-select");

  const trigger = createElement(container, "div", "env-select-trigger");
  const triggerSwatch = createElement(trigger, "div", "env-select-swatch");
  const triggerLabel = createElement(trigger, "span", "env-select-label");
  createElement(trigger, "span", "env-select-arrow");

  const list = createElement(container, "div", "env-select-list");

  for (let i = 0; i < presets.length; i++) {
    const preset = presets[i];
    const option = createElement(list, "div", "env-select-option");
    option.dataset.index = i;

    const swatch = createElement(option, "div", "env-select-swatch");
    swatch.style.background = presetGradientCSS(preset);

    const name = createElement(option, "span", "env-select-option-name");
    setLocalizedText(name, preset.name);

    option.addEventListener("click", () => {preferences.set(saveKey, i);list.classList.remove("open");});
  }

  let focusedIndex = -1;

  const setFocused = (index) => {
    const options = list.querySelectorAll(".env-select-option");
    if (index < 0) index = options.length - 1;
    if (index >= options.length) index = 0;

    for (const option of options) option.classList.remove("focused");
    options[index].classList.add("focused");
    options[index].scrollIntoView({ block: "nearest" });
    focusedIndex = index;
  };

  container.setAttribute("tabindex", "0");

  const currentIndex = () => {
    let index = preferences.get(saveKey);
    if (typeof index === "string") index = presets.findIndex((p) => p.name === index);
    if (!Number.isInteger(index) || index < 0 || index >= presets.length) index = 0;
    return index;
  };

  const openList = () => {list.classList.add("open");setFocused(currentIndex());};
  const closeList = () => {
    list.classList.remove("open");
    for (const option of list.querySelectorAll(".env-select-option")) option.classList.remove("focused");
    focusedIndex = -1;
  };

  trigger.addEventListener("click", () => list.classList.contains("open") ? closeList() : openList());

  const selectIndex = (index) => {
    if (index < 0) index = presets.length - 1;
    if (index >= presets.length) index = 0;
    preferences.set(saveKey, index);
    list.classList.contains("open") && setFocused(index);
  };

  container.addEventListener("keydown", (event) => {
    const isOpen = list.classList.contains("open");

    if (event.key === "ArrowDown") {
      event.preventDefault();
      event.stopPropagation();
      selectIndex(isOpen ? focusedIndex + 1 : currentIndex() + 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      event.stopPropagation();
      selectIndex(isOpen ? focusedIndex - 1 : currentIndex() - 1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      isOpen ? closeList() : openList();
    } else if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      closeList();
    }
  });

  const onDocClick = (event) => container.contains(event.target) || closeList();
  _document.addEventListener("click", onDocClick, true);

  const updateSelection = () => {
    const index = currentIndex();
    triggerSwatch.style.background = presetGradientCSS(presets[index]);
    setLocalizedText(triggerLabel, presets[index].name);

    const options = list.querySelectorAll(".env-select-option");
    for (const option of options) option.classList.toggle("selected", Number(option.dataset.index) === index);
  };

  updateSelection();
  preferences.addListeners(saveKey, updateSelection);

  this._envSelectContainer = container;
  this._envSelectDocClickHandler = onDocClick;
  this._envSelectUpdateHandler = updateSelection;

  const tabIndex = this.tabIdToIndex[tabId];
  const table = this.tablesContainer.childNodes[tabIndex];
  const row = table.tBodies[0].insertRow(-1);
  row.classList.add("switch-slider-row");

  const captionCell = row.insertCell(0);
  const label = createElement(captionCell, "div");
  setLocalizedText(label, "Environment Preset");

  const controlCell = row.insertCell(1);
  controlCell.colSpan = "2";
  controlCell.style.paddingRight = '16px';
  controlCell.appendChild(container);
};


/**
 * Updates the environment dropdown to reflect the current light preset selection.
 * Backward-compatible: silently no-ops if the environment section hasn't been created yet.
 */
ViewerSettingsPanel.prototype.updateEnvironmentSelection = function () {
  this._envSelectUpdateHandler?.();
};

/**
 * Removes an option from the given tab.
 *
 * @param {HTMLElement} checkBoxElem - checkbox to remove.
 * @returns {boolean} - True if the checkbox was removed.
 */
ViewerSettingsPanel.prototype.removeCheckbox = function (checkBoxElem)
{
  this.preferences.removeListeners(checkBoxElem.saveKey);
  this.removeEventListener(checkBoxElem, "change", checkBoxElem.changeListener);

  return SettingsPanel.prototype.removeCheckbox.call(this, checkBoxElem);
};

/**
 *  Populates the navigation tab with the appropriate checkboxes.
 */
ViewerSettingsPanel.prototype.createNavigationPanel = function ()
{
  var navTab = ViewerSettingTab.Navigation;

  if (this.is3dMode) {

    this.addLabel(navTab, "ViewCube");

    this.addCheckbox(navTab, "Show ViewCube", "Toggles availability of the ViewCube navigation control", true, undefined, Prefs3D.VIEW_CUBE);

    if (!isMobileDevice()) {
      this.addCheckbox(navTab, "ViewCube acts on pivot",
      "When enabled, the ViewCube orbits the view around the active pivot point When disabled, it orbits around the center of the view",
      false,
      undefined, Prefs3D.ALWAYS_USE_PIVOT);
    }

    this.addLabel(navTab, "Orbit");

    this.fusionStyleOrbitChkBoxId = this.addCheckbox(navTab, "Fusion style orbit", "Enables Fusion-style orbit overlay and gives the ability to lock orbit axis", false, (checked) => {
      // This is a hack
      // We want the extension to be loaded and unloaded by preferences, but unloading Fusion orbit when entering 2D
      // has a side effect of restoring default state of the 'orbit' tool, causing orbit to be active in 2D mode
      // Instead of preferences, we let fusion orbit extension loading controlled by the toggle only
      if (checked) {
        this.options.loadExtension('Autodesk.Viewing.FusionOrbit', null);
      } else {
        this.options.unloadExtension('Autodesk.Viewing.FusionOrbit', null);
      }
    }, Prefs.FUSION_ORBIT);

    this.addCheckbox(navTab, "Orbit past world poles", "Allows view rotation to continue past the model’s North Pole", true, undefined, Prefs3D.ORBIT_PAST_WORLD_POLES);

    this.addLabel(navTab, "Zoom");

    if (!isMobileDevice()) {
      this.addCheckbox(navTab, "Zoom towards pivot", "When disabled, zooming operations are centered at the current cursor location", false, undefined, Prefs3D.ZOOM_TOWARDS_PIVOT);

      this.addCheckbox(navTab, "Reverse mouse zoom direction", "Toggles direction of zooming in and out", false, undefined, Prefs.REVERSE_MOUSE_ZOOM_DIR);

      this.scrollSpeed = initScrollSpeed.call(this);
    }

    this.dragSpeed = initDragSpeed.call(this);

    // This label should probably be called something else for mobile.
    this.addLabel(navTab, "Mouse");
    if (!isMobileDevice()) {
      this.addCheckbox(navTab, "Left handed mouse setup", "Swaps the buttons on the mouse", false, undefined, Prefs.LEFT_HANDED_MOUSE_SETUP);

      this.addCheckbox(navTab, "Set pivot with left mouse button", "Change left-click behavior to set new pivot point (overrides select object)", false, undefined, Prefs3D.CLICK_TO_SET_COI);
    }
    this.addCheckbox(navTab, "Open properties on select", "Always show properties upon selecting object", true, undefined, Prefs.OPEN_PROPERTIES_ON_SELECT);
  }

  if (!this.is3dMode) {

    this.addLabel(navTab, "Zoom");

    this.addCheckbox(navTab, "Reverse mouse zoom direction", "Toggles direction of zooming in and out", false, undefined, Prefs.REVERSE_MOUSE_ZOOM_DIR);

    if (!isMobileDevice()) {
      this.scrollSpeed = initScrollSpeed.call(this);
    }

    this.dragSpeed = initDragSpeed.call(this);

    this.addLabel(navTab, "Mouse");

    this.addCheckbox(navTab, "Open properties on select", "Always show properties upon selecting object", true, undefined, Prefs.OPEN_PROPERTIES_ON_SELECT);

    if (!isMobileDevice()) {
      this.addCheckbox(navTab, "Left handed mouse setup", "Swaps the buttons on the mouse", false, undefined, Prefs.LEFT_HANDED_MOUSE_SETUP);
    }

  }

  // Creates the drag speed slider
  function initDragSpeed() {
    return this.addSliderV2(navTab, 'Drag Speed', 'Changes sensitivity of mouse movement with the zoom tool', 5, 300, this.options.initialZoomDragSpeed,
    { step: 5 }, Prefs.ZOOM_DRAG_SPEED)[1];
  }

  // Creates the scroll speed slider
  function initScrollSpeed() {
    return this.addSliderV2(navTab, 'Scroll Speed', 'Changes sensitivity of the mouse scroll wheel when zooming', 0.1, 3.0, this.options.initialZoomScrollScale,
    { step: 0.1 }, Prefs.ZOOM_SCROLL_SPEED)[1];
  }
};

/**
 * Adds a button to the configuration tab. Invokes a callback when end-users click on the button.
 * @param {string} label - Button's user-facing text
 * @param {function} onClickCb - Callback that will be called when the tool is clicked.
 * @returns {string} An identifier required to remove the button from the panel.
 */
ViewerSettingsPanel.prototype.addConfigButton = function (label, onClickCb) {

  if (!onClickCb)
  throw new Error('Must register a function callback.');

  // Add the Tools label to the Configuration tab
  if (!this._externalButtonsLabel)
  this._externalButtonsLabel = this.addLabel(ViewerSettingTab.Configuration, 'More');

  // Add button
  var btnId = this.addButton(ViewerSettingTab.Configuration, label);
  var btn = this.getControl(btnId);
  btn.setOnClick(() => {
    this.setVisible(false);
    onClickCb();
  });
  this._externalButtonIds.push(btnId);
  return btnId;
};

/**
 *
 * @param {number} tabId - Tab to add the new control
 * @param {string} label - The text associated with the button
 * @param {string} description - A description for the button
 * @param {string} buttonName - The name of the button
 * @param {function} onClickCb - Callback that will be called when the button is clicked
 * @param {object} [options] - Additional options for the button
 * @returns {string} An identifier required to remove the button from the panel
 */
ViewerSettingsPanel.prototype.addActionButton = function (tabId, label, description, buttonName, onClickCb, options) {
  var btnId = this.addButton(tabId, buttonName);
  var btn = this.getControl(btnId);
  const sliderRow = btn.sliderRow;
  while (sliderRow.cells.length > 0) {
    sliderRow.deleteCell(0);
  }
  sliderRow.classList.add("switch-slider-row");
  const _document = this.getDocument();

  var labelElem = _document.createElement("div");
  labelElem.setAttribute("data-i18n", label);
  labelElem.textContent = i18n.t(label, options?.i18nOptions);
  labelElem.classList.add("settings-row-title");
  var labelCell = sliderRow.insertCell();
  labelCell.appendChild(labelElem);

  var descriptionElem = _document.createElement("div");
  descriptionElem.setAttribute("data-i18n", description);
  descriptionElem.textContent = i18n.t(description, options?.i18nOptions);

  var descriptionCell = sliderRow.insertCell();
  descriptionCell.appendChild(descriptionElem);

  var buttonCell = sliderRow.insertCell();
  buttonCell.appendChild(btn.caption);
  btn.caption.classList.add("table-action-button");
  buttonCell.classList.add("settings-row-button");
  btn.setOnClick(() => {
    onClickCb();
    if (options.isOneTimer) {
      btn.caption.style.pointerEvents = "none";
      btn.caption.style.opacity = 0.3;
    }
    debounce(sendButtonAnalytics, 1000)(label, buttonName);
  });

  return btnId;
};

/**
 * Removes a config button from the Configuration tab.
 * @param {string} buttonId - Identifier obtained via {@link #addConfigButton}.
 * @returns {boolean} True if the button was removed.
 */
ViewerSettingsPanel.prototype.removeConfigButton = function (buttonId) {

  var index = this._externalButtonIds.indexOf(buttonId);
  if (index === -1)
  return false;

  var btn = this.getControl(buttonId); // btn should always be present at this stage.
  if (!btn)
  return false;

  this.removeControl(btn);
  this._externalButtonIds.splice(index, 1);

  // Remove label when no config buttons are present
  if (this._externalButtonIds.length === 0 && this._externalButtonsLabel !== null) {
    this._externalButtonsLabel.removeFromParent();
    this._externalButtonsLabel = null;
  }
  return true;
};

ViewerSettingsPanel.prototype.updateEdgeToggle = function () {
  const ctrl = document.getElementById(this.edgeCheckboxName + "_check");
  if (!ctrl) {
    // ctrl can be undefined when GEOMETRY_LOADED_EVENT event is fired before the config panel is initialized
    // It is ok to skip updating the toggle
    return;
  }
  if (!this.is3dMode) {
    return;
  }
  const hasEdges = this.options.detectIfModelsHaveEdge();
  if (hasEdges) {
    ctrl.disabled = false;
  } else {
    ctrl.disabled = true;
    ctrl.checked = false;
  }
};

async function clearOriginPrivateFileSystem() {
  navigator.storage.
  estimate().
  then((estimate) => {
    avp.analytics.track('viewer.opfsCache.clearButton', estimate);
  }).
  catch((error) => {
    console.error('Failed to get storage estimate', error);
  });

  const geomCache = this.viewer.impl.geomCache();
  await (geomCache?.clearOpfsCache && geomCache.clearOpfsCache());
}

// This function ensures that the UI toggle for LargeModelExperience is forced-on when activating the WebGPU toggle.
// Note this is about the UI state only, it doesn't enable the actual feature.
function onWebGPUToggled(panel, enableWebGPU, options) {
  const webgpuControl = panel.getControl(panel.webgpuChkBoxId);
  if (webgpuControl) {
    const descriptionCell = webgpuControl.description.parentElement;
    let reloadElem = descriptionCell.querySelector('.reload-message');

    if (!reloadElem) {
      const _document = panel.getDocument();
      reloadElem = _document.createElement('div');
      reloadElem.className = 'reload-message';
      reloadElem.style.color = 'red';
      descriptionCell.appendChild(reloadElem);
    }

    if (enableWebGPU !== USE_WEBGPU) {
      const reloadMsgKey = 'Reload the page to apply this setting';
      reloadElem.setAttribute('data-i18n', reloadMsgKey);
      reloadElem.textContent = i18n.t(reloadMsgKey, options?.i18nOptions);
      reloadElem.style.display = 'block';
    } else {
      reloadElem.style.display = 'none';
    }
  }

  // Get large-model experience toggle
  const lmeControl = panel.getControl(panel.largeModelExperienceChkBoxId);

  // Toggle may not exist (if we are in 2D mode). In this case, we don't need to do anything.
  if (!lmeControl) {
    return;
  }

  // Purpose of this function is to force-on the large-model experience toggle if WebGPU is on.
  // This is only useful if both toggles are actually there. (Large Model Experience toggle is always there meanwhile)
  const webGPUToggleExists = av.FeatureFlags.isEnabled(InternalFeatureFlags.WebGPUBackend);
  if (!webGPUToggleExists) {
    return;
  }

  if (enableWebGPU) {
    // Force toggle on. Note that this just enables the UI toggle, not changing actual prefs.
    lmeControl.setValue(true);

    // Disable clicking the toggle as long as WebGPU is on
    lmeControl.setDisabled(true);

    // Disabling the control apparently just blocks it from using, but doesn't give any visual hint.
    // So, fade it out a bit to indicate that disabling is not a bug.
    lmeControl.sliderRow.style.opacity = 0.5;

  } else {
    // Recover actual toggle state for Large-Model experience if WebGPU is toggled off
    lmeControl.setDisabled(false);
    lmeControl.sliderRow.style.opacity = "";

    // Re-sync toggle to actual prefs value.
    // Toggling WebGPU on/off should never accidentally disable the large model experience.
    // Therefore, we recover the original state from settings if toggling WebGPU off again
    const largeModelPrefOn = panel.preferences.get(Prefs3D.LARGE_MODEL_EXPERIENCE);
    lmeControl.setValue(largeModelPrefOn);
  }
}

function addFeatureFlagToggles(panel, tabId, is3dMode) {

  // Avoid i18n from eating ":" and "." characters. If we need that more often, we should share it
  // somewhere or consider a general change in i18n config.
  const i18nOptions = { nsSeparator: false, keySeparator: false };

  if (is3dMode && !av.FeatureFlags.isEnabled(InternalFeatureFlags.EnforceLargeModelExperience)) {
    const description = 'Improves your overall experience viewing large models. The changes will take effect on the next page load.';
    panel['largeModelExperienceChkBoxId'] = panel.addCheckbox(tabId, 'Large model experience',
    description, false, undefined, Prefs3D.LARGE_MODEL_EXPERIENCE, { i18nOptions });
  }

  const webGPUToggleExists = av.FeatureFlags.isEnabled(InternalFeatureFlags.WebGPUBackend);
  if (webGPUToggleExists) {
    const isWebGPUSupported = isWebGPUSupported_cached();

    const description = isWebGPUSupported ? 'Use latest browser technology for 3D web graphics (WebGPU)' : 'WebGPU is not supported by this browser';
    const initialState = (webGPUEnabled) => {onWebGPUToggled(panel, webGPUEnabled, { i18nOptions });};

    panel['webgpuChkBoxId'] = panel.addCheckbox(tabId, 'WebGPU Graphics',
    description, false, initialState, Prefs.WEBGPU, { i18nOptions });

    // Disable settings toggle for unsupported clients.
    if (!isWebGPUSupported) {
      const control = panel.getControl(panel['webgpuChkBoxId']);
      // Force toggle off. Note that this just sets the UI toggle, the actual pref is unchanged.
      control.setValue(false);
      // Make the toggle unclickable.
      control.setDisabled(true);
      // Fade out toggle a bit to indicate that it is disabled.
      control.sliderRow.style.opacity = 0.5;
    }
  }

  if (av.FeatureFlags.isEnabled(InternalFeatureFlags.EnableGPUMemorySetting) && USE_WEBGPU) {
    const items = Object.keys(GPU_MEMORY_PRESETS);
    const values = Object.values(GPU_MEMORY_PRESETS);
    panel.addDropDownMenu(tabId, "Maximum GPU Memory", items, values, null, Prefs3D.GPU_MEMORY_LIMIT);
  }
}

/** Populates the Configuration tab with the appropriate checkboxes.
 *
 */
ViewerSettingsPanel.prototype.createConfigurationPanel = function () {
  var configTab = ViewerSettingTab.Configuration;

  if (this.is3dMode) {

    this.addLabel(configTab, "Performance Optimization");

    this.optimizeNavigationhkBoxId = this.addCheckbox(configTab, "Disable shadow effect", "Provides faster response(but degrades quality) while navigating",
    isMobileDevice(), undefined, Prefs3D.OPTIMIZE_NAVIGATION);

    const progressiveRenderDesc = "Uses incremental updates of the view for a more responsive interaction with the model Use in combination with the Target Frame Rate setting to balance interactivity with model elements flickering";

    // If fps target dropdown is available, disable it when progressive rendering is off
    const onProgressiveRenderChange = (value) => {
      if (this.progressiveFpsTargetId) {
        const fpsTargetControl = this.getControl(this.progressiveFpsTargetId);
        fpsTargetControl.setDisabled(!value);
        fpsTargetControl.sliderRow.style.opacity = value ? "" : 0.5;
      }
    };
    this.progressiveRenderChkBoxId = this.addCheckbox(configTab, "Progressive display", progressiveRenderDesc,
    true, onProgressiveRenderChange, Prefs.PROGRESSIVE_RENDERING);

    // When changing anything here, also update the strings in res/locales/en/viewer-settings.loc.json
    const items = Object.entries(FPS_TARGET_MODES).map(([key, value]) => key.charAt(0) + key.substring(1).toLowerCase() + ' (' + value + ')');
    const values = Object.values(FPS_TARGET_MODES);
    this.progressiveFpsTargetId = this.addDropDownMenu(configTab, "Target Frame Rate", items, values, null, Prefs.PROGRESSIVE_FPS_TARGET);

    addFeatureFlagToggles(this, configTab, this.is3dMode);

    this.clearOpfsCacheBtnId = this.addActionButton(configTab,
    'Clear model cache',
    'Frees up browser storage space by deleting cached data of opened models.',
    'Clear',
    clearOriginPrivateFileSystem.bind(this),
    { isOneTimer: true }
    );

    this.addLabel(configTab, "Display");

    this.ghosthiddenChkBoxId = this.addCheckbox(configTab, "Ghost hidden objects", "Leave hidden objects slightly visible",
    true, undefined, Prefs3D.GHOSTING);

    this.displayLinesId = this.addCheckbox(configTab, "Display Lines", "Toggles display of line objects", true, undefined, Prefs3D.LINE_RENDERING);


    this.displayPointsId = this.addCheckbox(configTab, "Display Points", "Toggles display of point objects", true, undefined, Prefs.POINT_RENDERING);

    this.edgeCheckboxName = "Display edges";
    this.displayEdgesId = this.addCheckbox(configTab, this.edgeCheckboxName, "Shows outline of model surfaces", false, (value) => {
      const edgeCheckbox = this.getControl(this.displayEdgesId);
      edgeCheckbox.setValue(value);
      this.updateEdgeToggle();
    }, Prefs3D.EDGE_RENDERING);
    this.updateEdgeToggle();

    if (!av.FeatureFlags.isEnabled(avp.InternalFeatureFlags.EnableSmartSection)) {
      this.displaySectionHatchesId = this.addCheckbox(
        configTab,
        'Display Section Hatches',
        'Shows hatch pattern for section planes this does not apply to section boxes',
        true,
        null,
        avp.Prefs3D.DISPLAY_SECTION_HATCHES
      );
    }
  } else {
    // 2D only

    this.addLabel(configTab, "Performance Optimization");

    this.progressiveRenderChkBoxId = this.addCheckbox(configTab, "Progressive display", "Shows incremental updates of the view and allows for more responsive interaction with the model (some elements may flicker) This improves perceived waiting time",
    true, undefined, Prefs.PROGRESSIVE_RENDERING);

    addFeatureFlagToggles(this, configTab, this.is3dMode);

    if (this.isPdfFile) {
      this.vectorViewingChkBoxId = this.addCheckbox(configTab, "Vector Viewing", "Provides increased zooming, snapping and access to PDF vector data. If turned off, all PDFs will be rasterized.",
      true, undefined, Prefs2D.VECTOR_VIEWING);
    }

    this.addLabel(configTab, "Display");
  }

  // 2d or 3d
  // Anything added below this line applies to "Display" subtitle
  this.displayUnitsId = this.addDropDownMenu(configTab, 'Display Units', avp.displayUnits, avp.displayUnitsEnum, null, avp.Prefs.DISPLAY_UNITS);
  this.displayUnitsPrecisionId = this.addDropDownMenu(configTab, 'Precision', avp.displayUnitsPrecision, avp.displayUnitsPrecisionEnum, null, avp.Prefs.DISPLAY_UNITS_PRECISION);

  if (this.is3dMode) {
    this._addSelectionModeOption();
  }
};

ViewerSettingsPanel.prototype._addSelectionModeOption = function () {

  const configTab = ViewerSettingTab.Configuration;

  this.addLabel(configTab, "Selection");

  // Convert enum into arrays
  const selectionModes = [];
  const selectionModeValues = [];
  let i = 0;
  for (let key in Autodesk.Viewing.SelectionMode) {
    selectionModes[i] = key;
    selectionModeValues[i] = Autodesk.Viewing.SelectionMode[key];
    i++;
  }
  // Capitalize the mode names for display
  const names = selectionModes.map((mode) =>
  mode.
  split('_').
  map((str) => str.charAt(0) + str.slice(1).toLowerCase()).
  join(' ')
  );

  this.selectionModeId = this.addDropDownMenu(configTab, 'Selection Mode', names, selectionModeValues, null, avp.Prefs3D.SELECTION_MODE);
};

/**
 * Populates the appearance tab with the appropriate checkboxes.
 */

ViewerSettingsPanel.prototype.createAppearancePanel = function () {
  var appearTab = ViewerSettingTab.Appearance;
  if (this.is3dMode) {
    this.antialiasingChkBoxId = this.addCheckbox(appearTab, "Anti-Aliasing", "Smooth out jagged edges using FXAA", true, undefined, Prefs3D.ANTIALIASING);
    this.ambientshadowsChkBoxId = this.addCheckbox(appearTab, "Ambient Shadows", "Improve shading of occluded surfaces", true, undefined, Prefs3D.AMBIENT_SHADOWS);
    this.groundShadowChkBoxId = this.addCheckbox(appearTab, "Ground Shadow", "Add simulated ground surface shadows", true, undefined, Prefs3D.GROUND_SHADOW);

    const gsSupportedDescription = 'Add simulated ground surface reflections';
    const gsUnsupportedDescription = 'Not supported in WebGPU';
    this.groundReflectionChkBoxId = this.addCheckbox(appearTab, "Ground Reflection",
    USE_WEBGPU ? gsUnsupportedDescription : gsSupportedDescription, false, undefined, Prefs3D.GROUND_REFLECTION);

    // @todo: review and potentially generalize this behavior, e.g., move to addCheckbox.
    // Instead of showing the actual state of the setting, if its not supported at all, we hide the checkbox itself
    // and make the whole slider-row uncheckable and translucent.
    if (USE_WEBGPU) {
      const control = this.getControl(this.groundReflectionChkBoxId);
      control.setValue(false);
      control.setDisabled(true);
      control.sliderRow.style.opacity = 0.5;
      // <td><label class="switch"><input type="checkbox" .../><div class="slider" /></label></td>
      const td = control.checkElement.parentElement.parentElement;
      td.hidden = true;
    }

    this.createEnvironmentSection(appearTab);
  } else {
    this.swapBlackAndWhiteChkBoxId = this.addCheckbox(appearTab, "2D Sheet Color", "Switch sheet color white to black", true, undefined, Prefs2D.SWAP_BLACK_AND_WHITE);
    this.loadingAnimationChkBoxId = this.addCheckbox(appearTab, "Loading Animation", "Animate lines during loading", true, undefined, Prefs2D.LOADING_ANIMATION);
  }
};


/**
 * Creates the environment section inside the Appearance tab. Includes the env-map background toggle
 * and a compact dropdown for light preset selection with gradient previews.
 * @private
 */
ViewerSettingsPanel.prototype.createEnvironmentSection = function (tabId) {
  this.addEnvironmentDropDown(tabId, LightPresets, avp.Prefs3D.LIGHT_PRESET);
  this.envMapBackgroundChkBoxId = this.addCheckbox(tabId, "Environment Image Visible", "Shows lighting environment as background", true, undefined, Prefs3D.ENV_MAP_BACKGROUND);
};


/**
 * Experiment definitions for the experimental settings tab. Each entry describes one toggleable experiment
 * with its preference key, display strings, mode restrictions, and whether it's currently enabled.
 * @returns {Array<{saveKey: string, name: string, description: string, is3dOnly: boolean, initialState: boolean, enabled: boolean}>}
 * @private
 */
ViewerSettingsPanel.prototype._getExperimentalSettings = function () {
  const experiments = [
  {
    saveKey: avp.Prefs3D.ANTIALIASING_MSAA,
    name: "Multisample Anti-Aliasing",
    description: "Enable 4xMSAA for improved visual quality of edges and contours",
    is3dOnly: true,
    initialState: false,
    enabled: true
  }];


  if (!av.FeatureFlags.isEnabled(InternalFeatureFlags.EnableGPUMemorySetting)) {
    experiments.push({
      type: 'dropdown-secondary',
      saveKey: avp.Prefs3D.GPU_MEMORY_LIMIT,
      name: "Maximum GPU Memory",
      description: "Setup the amount of GPU memory (VRAM) available for rendering",
      items: Object.keys(GPU_MEMORY_PRESETS),
      values: Object.values(GPU_MEMORY_PRESETS),
      is3dOnly: true,
      enabled: true
    });
  }

  return experiments;
};

/**
 * Creates the experimental overflow menu and hidden tab content. The three-dot kebab icon replaces a normal
 * tab label; clicking it opens a small dropdown with "Experimental Features" which then reveals the tab
 * content. Bails out early when no experiment is available for the current mode.
 * @private
 */
ViewerSettingsPanel.prototype._initExperimentalTab = function () {
  const experiments = this._getExperimentalSettings().filter((exp) => !exp.is3dOnly || this.is3dMode);
  if (experiments.length === 0) return;

  this.addTab(ViewerSettingTab.Experimental, "Experimental", { className: "experimental" });
  this.tablesContainer.childNodes[this.tabIdToIndex[ViewerSettingTab.Experimental]].classList.add("pt-4");

  const tabElement = this.panelTabs[this.tabIdToIndex[ViewerSettingTab.Experimental]];
  const tabSpan = tabElement.querySelector("span");
  if (tabSpan) {
    tabSpan.textContent = "";
    tabSpan.classList.add("experimental-tab-icon", "adsk-more-vertical-icon", "icon-size-16");
    tabSpan.style.borderBottom = "none";
  }

  tabElement.style.position = "relative";
  const menu = createElement(tabElement, "div", "experimental-overflow-menu");

  const menuItem = createElement(menu, "div", "experimental-overflow-item");
  setLocalizedText(menuItem, "Experimental Features");

  const toggleMenu = () => menu.classList.toggle("open");
  const hideMenu = () => menu.classList.remove("open");

  tabElement.addEventListener("click", (event) => {
    if (menu.contains(event.target)) return;
    event.stopPropagation();
    event.stopImmediatePropagation();
    toggleMenu();
  }, true);

  menuItem.addEventListener("click", (event) => {
    event.stopPropagation();
    event.stopImmediatePropagation();
    hideMenu();
    this.selectTab(ViewerSettingTab.Experimental);
  });

  const onDocClick = (event) => tabElement.contains(event.target) || hideMenu();
  this.getDocument().addEventListener("click", onDocClick, true);
  this._experimentalMenuDocClickHandler = onDocClick;

  createExperimentalPanel(this, ViewerSettingTab.Experimental, experiments);

  avp.analytics.track('viewer.settings.experimental_tab_available', {
    experiment_count: experiments.length,
    experiment_keys: experiments.map((e) => e.saveKey)
  });
};

/**
 * Adds viewer version label to Footer div.
 */
ViewerSettingsPanel.prototype.createVersionLabel = function (parent) {

  if (!parent)
  return;
  var _document = this.getDocument();
  this.versionDiv = _document.createElement('div');
  this.versionDiv.textContent = getVersionString(this.options.version); // No need to localize.
  this.versionDiv.className = 'docking-panel-version-label';
  this.addEventListener(this.versionDiv, 'click', (event) => {
    if (event.shiftKey) {
      navigator.clipboard.writeText(this.versionDiv.textContent);
    }
  });
  parent.appendChild(this.versionDiv);
};

/**
 * Create a restore default settings button. It is appended to the settings panel
 */
ViewerSettingsPanel.prototype.createRestoreDefaultSettingsButton = function () {

  var _document = this.getDocument();
  this.restoreDiv = _document.createElement('div');
  this.restoreDiv.classList.add('docking-panel-container-solid-color-b');
  this.restoreDiv.classList.add('restore-defaults-container');

  this.restoreButton = _document.createElement('div');
  this.restoreButton.className = 'docking-panel-tertiary-button';
  setLocalizedText(this.restoreButton, "Restore all default settings");
  this.restoreDiv.appendChild(this.restoreButton);

  this.addEventListener(this.restoreDiv, 'touchstart', touchStartToClick);
  this.addEventListener(this.restoreDiv, 'click', () => {
    this.options.onRestoreDefaults?.();
    avp.analytics.track('viewer.settings.default');
  }, false);

  this.scrollContainer.appendChild(this.restoreDiv);
};


ViewerSettingsPanel.prototype.selectTab = function (tabId) {
  SettingsPanel.prototype.selectTab.call(this, tabId);
  this.sizeToContent();

  if (tabId === ViewerSettingTab.Experimental) {
    avp.analytics.track('viewer.settings.experimental_tab_viewed');
  }
};

/**
 * Resizes panel vertically to wrap around the content.
 * It will always leave some room at the bottom to display the toolbar.
 */
ViewerSettingsPanel.prototype.sizeToContent = function () {
  SettingsPanel.prototype.sizeToContent.call(this, this.options.container);
};

ViewerSettingsPanel.prototype.onViewerResize = function (vt, vb, vl, vr, vw, vh) {
  // Avoid default behavior by overriding inherited implementation.
  this.sizeToContent();
};

/**
 * Updates the values in the checkboxes based on what is in the prefs.
 */
ViewerSettingsPanel.prototype.syncUI = function () {
  var prefs = this.preferences;

  this.setControlValue(this.antialiasingChkBoxId, prefs.get('antialiasing'));
  this.setControlValue(this.ambientshadowsChkBoxId, prefs.get('ambientShadows'));
  this.setControlValue(this.groundShadowChkBoxId, prefs.get('groundShadow'));
  this.setControlValue(this.groundReflectionChkBoxId, prefs.get('groundReflection'));
  this.setControlValue(this.envMapBackgroundChkBoxId, prefs.get('envMapBackground'));
  this.setControlValue(this.progressiveRenderChkBoxId, prefs.get('progressiveRendering'));
  this.setControlValue(this.largeModelExperienceChkBoxId, prefs.get(Prefs3D.LARGE_MODEL_EXPERIENCE));
  this.setControlValue(this.webgpuChkBoxId, prefs.get(Prefs.WEBGPU));
  this.setControlValue(this.swapBlackAndWhiteChkBoxId, prefs.get('swapBlackAndWhite'));
  this.setControlValue(this.vectorViewingChkBoxId, prefs.get(Prefs2D.VECTOR_VIEWING));
  this.setControlValue(this.loadingAnimationChkBoxId, prefs.get('loadingAnimation'));
  this.setControlValue(this.ghosthiddenChkBoxId, prefs.get('ghosting'));
  this.setControlValue(this.displayLinesId, prefs.get('lineRendering'));
  this.setControlValue(this.displayPointsId, prefs.get('pointRendering'));
  this.setControlValue(this.displayEdgesId, prefs.get('edgeRendering'));
  this.setControlValue(this.displaySectionHatchesId, prefs.get('displaySectionHatches'));
  this.setControlValue(this.scrollSpeed, prefs.get('zoomScrollSpeed'));
  this.setControlValue(this.dragSpeed, prefs.get('zoomDragSpeed'));

  // Workaround: By default this line has no effect at all. Only if WebGPU feature flag is enabled and toggled on,
  //             it will auto-enable the large model experience toggle in the settings panel.
  const webGPUToggleExists = av.FeatureFlags.isEnabled(InternalFeatureFlags.WebGPUBackend);
  const webGPUIsEnabled = webGPUToggleExists && prefs.get(Prefs.WEBGPU) && isWebGPUSupported_cached();
  onWebGPUToggled(this, webGPUIsEnabled);

  this.updateEnvironmentSelection();
};


/**
 * Safely sets the value of a checkbox control.
 *
 * @param {string} ctrlName - The id of the control
 * @param {boolean} value
 */
ViewerSettingsPanel.prototype.setControlValue = function (ctrlName, value) {
  var ctrl = this.getControl(ctrlName);
  if (ctrl) {
    ctrl.setValue(value);
  }
};


function getVersionString(version = LMV_VIEWER_VERSION) {
  if (version.charAt(0) === "@") {
    version = '0.0.0'; // No need to localize.
  }
  return 'v' + version;
}


// This function is intentionally kept as a separate function and not inlined above
// to prevent the creation of a closure that reference the model and thus prevents
// garbage collection of the model object.
function sendAnalytics(name, value) {
  avp.analytics.track('viewer.settings.changed', {
    setting_name: name,
    setting_value: value
  });
}

function sendButtonAnalytics(label, buttonName) {
  avp.analytics.track('viewer.settings.button', {
    button_name: buttonName,
    label
  });
}

function detectIfModelsHaveEdge() {
  if (!this.is3dMode) {
    return false;
  }

  let hasEdges = false;
  const models = this.viewer.impl.modelQueue().getModels();
  for (let i = 0; i < models.length; i++) {
    let model = models[i];
    // hasEdges is undefined until the model is done loading. Don't prevent the user from using the toggle until then.
    if (model.hasEdges !== false) {
      hasEdges = true;
      break;
    }
  }

  return hasEdges;
}